<template>
  <section>
    <main
      class="main animated fadeIn faster"
      :class="{ 'expand-main': isMenuCollapse }"
    >
  
  <div class="tgv-wrap">
    <div class="tgv-head">
      <div>
        <span class="tgv-eyebrow">Account Management</span>
        <h1 class="tgv-title">Teams — Global view</h1>
        <p class="tgv-sub">All teams across every client. A client can have several teams.</p>
      </div>
      <div class="tgv-am-filter">
        <label class="tgv-am-label">Account Manager</label>
        <div class="tgv-select-wrap">
          <select v-model="amId" class="tgv-select" @change="onAmChange">
            <option :value="0">All AMs</option>
            <option v-for="am in ams" :key="am.id" :value="am.id">{{ am.name }}</option>
          </select>
          <i class="icon-angle-down tgv-select-caret"></i>
        </div>
      </div>
    </div>

    <LoadingSpinner v-if="fullLoading" />
    <template v-else>
    <div class="tgv-cards">
      <div class="tgv-card">
        <span class="tgv-card-value">{{ summary.total }}</span>
        <span class="tgv-card-label">Total teams</span>
      </div>
      <div class="tgv-card tgv-card--green">
        <span class="tgv-card-value">{{ summary.in_use }}</span>
        <span class="tgv-card-label">In use</span>
      </div>
      <div class="tgv-card">
        <span class="tgv-card-value">{{ summary.available }}</span>
        <span class="tgv-card-label">Available</span>
      </div>
      <div class="tgv-card tgv-card--amber">
        <span class="tgv-card-value">{{ summary.pending }}</span>
        <span class="tgv-card-label">Pending delete</span>
      </div>
      <div class="tgv-card tgv-card--red">
        <span class="tgv-card-value">{{ summary.deletion_lte3 }}</span>
        <span class="tgv-card-label">Deletion ≤ 3 days</span>
      </div>
    </div>

    <div class="tgv-tabs">
      <button
        v-for="t in tabItems"
        :key="t.key"
        type="button"
        class="tgv-tab"
        :class="{ 'tgv-tab--active': tab === t.key }"
        @click="onTabChange(t.key)"
      >
        {{ t.label }}
        <span class="tgv-tab-count">{{ tabs[t.key] }}</span>
      </button>
    </div>

    <div v-if="tab === 'pending'" class="tgv-chips">
      <button
        type="button"
        class="tgv-chip"
        :class="{ 'tgv-chip--active': condition === null }"
        @click="onConditionChange(null)"
      >
        All reasons
        <span class="tgv-chip-count">{{ summary.pending }}</span>
      </button>
      <button
        v-for="c in conditionItems"
        :key="c.code"
        type="button"
        class="tgv-chip"
        :class="{ 'tgv-chip--active': condition === c.code }"
        @click="onConditionChange(c.code)"
      >
        <span class="tgv-chip-dot" :style="{ background: c.color }"></span>
        {{ c.code }} · {{ c.label }}
        <span class="tgv-chip-count">{{ conditions[c.code] }}</span>
      </button>
    </div>

    <div v-if="tab === 'pending'" class="tgv-banner">
      <i class="icon-info tgv-banner-ico"></i>
      <p class="tgv-banner-text">
        Each team below matched a deletion condition and entered a <strong>15-day countdown</strong>
        (from <strong>free since</strong>) — it will be <strong>deleted automatically</strong> at the end.
        The countdown is <strong>cancelled automatically</strong> if the team is used again (order launched,
        or attached to a running order). You'll be notified <strong>7 days</strong> and <strong>3 days</strong>
        before each deletion. If you need more time, use <strong>+ Days</strong>.
      </p>
    </div>

    <div class="tgv-searchbar">
      <div class="tgv-search">
        <i class="icon-search tgv-search-ico"></i>
        <input
          v-model="search"
          type="text"
          class="tgv-search-input"
          placeholder="Search a team, a client..."
          @input="onSearchInput"
        />
      </div>
    </div>

    <LoadingSpinner v-if="loading"  />
    <div v-else-if="error" class="tgv-state tgv-state--error">{{ error }}</div>
    <div v-else-if="!rows.length" class="tgv-state">No teams match the current filters.</div>

    <template v-else>
      <TableV2
        :headers="headers"
        :cols="cols"
        :data="rows"
        @goToOrder="goToOrder"
        @addDays="onAddDays"
      />

      <div class="tgv-footer">
        <span class="tgv-footer-info">
          {{ rangeStart }}–{{ rangeEnd }} of {{ total }} teams
        </span>
        <div class="tgv-footer-right">
          <div class="tgv-select-wrap tgv-select-wrap--sm">
            <select v-model.number="size" class="tgv-select tgv-select--sm" @change="onSizeChange">
              <option v-for="s in sizeOptions" :key="s" :value="s">{{ s }} / page</option>
            </select>
            <i class="icon-angle-down tgv-select-caret"></i>
          </div>
          <div class="tgv-pager">
            <button
              type="button"
              class="tgv-pager-btn"
              :disabled="page <= 1"
              @click="goToPage(page - 1)"
            >&laquo;</button>
            <span class="tgv-pager-info">Page {{ page }} / {{ totalPages }}</span>
            <button
              type="button"
              class="tgv-pager-btn"
              :disabled="page >= totalPages"
              @click="goToPage(page + 1)"
            >&raquo;</button>
          </div>
        </div>
      </div>
    </template>
    </template>
  </div>

  <div v-if="showPostponeModal" class="tgv-modal-overlay" @click.self="closePostponeModal">
    <div class="tgv-modal" role="dialog" aria-modal="true" aria-labelledby="tgv-modal-title">
      <h3 id="tgv-modal-title" class="tgv-modal-title">Postpone deletion</h3>
      <p class="tgv-modal-sub">
        Number of days to add to the deletion deadline.
      </p>

      <label class="tgv-modal-label" for="tgv-modal-days">Days</label>
      <input
        id="tgv-modal-days"
        ref="postponeInput"
        v-model="postponeDaysInput"
        type="number"
        min="1"
        :max="postponeMaxDays"
        step="1"
        inputmode="numeric"
        class="tgv-modal-input"
        :class="{ 'tgv-modal-input--error': !!postponeError }"
        :disabled="postponing"
        @keydown="onPostponeKeydown"
        @input="onPostponeInput"
        @keyup.enter="applyPostpone"
      />
      <p v-if="postponeError" class="tgv-modal-error">{{ postponeError }}</p>

      <div class="tgv-modal-actions">
        <button
          type="button"
          class="tgv-modal-btn tgv-modal-btn--ghost"
          :disabled="postponing"
          @click="closePostponeModal"
        >
          Cancel
        </button>
        <button
          type="button"
          class="tgv-modal-btn tgv-modal-btn--primary"
          :disabled="postponing || !isValidPostponeDays"
          @click="applyPostpone"
        >
          {{ postponing ? 'Applying…' : 'Apply' }}
        </button>
      </div>
    </div>
  </div>
  </main>
</section>
</template>

<script>
import TableV2 from '@/components/Common/TableV2.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { teamsOverviewApi } from '@/_services'
import { mapState } from "vuex";

const CONDITION_META = {
  C1: { code: 'C1', label: 'No order attached', color: '#c62828' },
  C2: { code: 'C2', label: 'Stopped / archived orders only', color: '#c62828' },
  C3: { code: 'C3', label: 'Draft never launched (>30d)', color: '#c78a0a' },
  C4: { code: 'C4', label: 'Preparation (> 45d)', color: '#c78a0a' },
}

export default {
  name: 'TeamsGlobal',
  components: { TableV2, LoadingSpinner },
  data() {
    return {
      loading: false,
      fullLoading: true,
      error: '',
      ams: [],
      summary: { total: 0, in_use: 0, available: 0, pending: 0, deletion_lte3: 0 },
      tabs: { all: 0, available: 0, pending: 0 },
      conditions: { C1: 0, C2: 0, C3: 0, C4: 0 },
      teams: [],
      total: 0,
      tab: 'pending',
      condition: null,
      amId: 0,
      search: '',
      page: 1,
      size: 25,
      sizeOptions: [12, 25, 50],
      searchTimer: null,
      tabItems: [
        { key: 'all', label: 'All teams' },
        { key: 'available', label: 'Available teams' },
        { key: 'pending', label: 'Pending delete teams' },
      ],
      conditionItems: [
        CONDITION_META.C1,
        CONDITION_META.C2,
        CONDITION_META.C3,
        CONDITION_META.C4,
      ],
      showPostponeModal: false,
      postponeTeamDeletionId: null,
      postponeDaysInput: 7,
      postponeMaxDays: 30,
      postponing: false,
      postponeError: '',
    }
  },
  computed: {
    ...mapState({
      isMenuCollapse: state => state.sidebar.isNavOpen,
    }),
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.size))
    },
    rangeStart() {
      if (!this.total) return 0
      return (this.page - 1) * this.size + 1
    },
    rangeEnd() {
      return Math.min(this.page * this.size, this.total)
    },
    rows() {
      return this.teams.map((team) => this.mapTeam(team))
    },
    headers() {
      const base = [
        { label: 'Team' },
        { label: 'Client' },
        { label: 'AM' },
        { label: 'Mailboxes' },
        { label: 'Linked order' },
        { label: 'Status' },
        { label: 'Free since', class: 'text-right' },
      ]
      if (this.tab === 'pending') {
        base.push({ label: 'Scheduled deletion', class: 'text-right' })
        base.push({ label: 'Actions', class: 'text-right', size: '110px' })
      }
      return base
    },
    isValidPostponeDays() {
      const days = Number(this.postponeDaysInput)
      return Number.isInteger(days) && days > 0 && days <= this.postponeMaxDays
    },
    cols() {
      const base = [
        [{ type: 'stackedText', primary_value: 'name', sub_value: 'team_sub' }],
        [{ value: 'client_name' }],
        [{ value: 'am_name' }],
        [{ value: 'mailboxes', class: 'font-weight-bold' }],
        [{
          type: 'linkedOrder',
          value_label: 'linked_order_label',
          value_emit: 'linked_order_id',
          stateField: 'linked_order_state',
          emit: 'goToOrder',
        }],
        [{ type: 'deletionStatus', value: 'status', sub_value: 'condition_label' }],
        [{ value: 'free_since_label', class: 'text-right tgv-free' }],
      ]
      if (this.tab === 'pending') {
        base.push([{
          type: 'scheduledDeletion',
          deadline_field: 'scheduled_deadline_label',
          days_field: 'scheduled_days_to_deadline',
          urgency_field: 'scheduled_urgency',
          added_field: 'scheduled_added_label',
          class: 'text-right',
        }])
        base.push([{
          type: 'emit',
          emit: 'addDays',
          value_emit: 'team_deletion_id',
          value: 'status',
          value_if: 'pending_delete',
          label: '+ Days',
          class: 'tgv-days-btn',
        }])
      }
      return base
    },
  },
  created() {
    this.load()
  },
  methods: {
    load() {
      this.loading = true
      this.error = ''
      teamsOverviewApi
        .getOverview({
          tab: this.tab,
          condition: this.condition,
          am_id: this.amId,
          q: this.search,
          page: this.page,
          size: this.size,
        })
        .then((data) => {
          this.ams = Array.isArray(data.ams) ? data.ams : []
          this.summary = data.summary || this.summary
          this.tabs = data.tabs || this.tabs
          this.conditions = data.conditions || this.conditions
          this.teams = Array.isArray(data.teams) ? data.teams : []
          this.total = data.total || 0
          this.page = data.page || this.page
          this.size = data.size || this.size
          this.loading = false
          this.fullLoading = false
        })
        .catch(() => {
          this.error = 'Unable to load teams. Please try again.'
          this.loading = false
          this.fullLoading = false
        })
    },
    mapTeam(team) {
      const client = team.client || {}
      const am = team.am || {}
      const order = team.linked_order || null
      const deletion = team.deletion || null

      return {
        team_deletion_id: team.team_deletion_id,
        id: team.id,
        name: team.name,
        team_sub: this.teamSubtitle(team),
        client_name: client.name || '—',
        am_name: am.name || '—',
        mailboxes: team.mailboxes,
        linked_order_label: order ? `# ${order.name} (${order.state})` : null,
        linked_order_state: order ? order.state : null,
        linked_order_id: order ? order.id : null,
        status: team.status,
        condition_label: this.conditionLabel(deletion),
        free_since_label: this.freeSinceLabel(deletion),
        scheduled_deadline_label: this.deadlineLabel(deletion),
        scheduled_days_to_deadline: this.deadlineDays(deletion),
        scheduled_urgency: this.deadlineUrgency(deletion),
        scheduled_added_label: this.addedByLabel(deletion),
      }
    },
    teamSubtitle(team) {
      const mailboxes = `${team.mailboxes} mailbox${team.mailboxes === 1 ? '' : 'es'}`
      const licenses = `${team.licenses} license${team.licenses === 1 ? '' : 's'}`
      return `(${team.id}) · ${mailboxes} · ${licenses}`
    },
    conditionLabel(deletion) {
      if (!deletion || !deletion.condition) return ''
      const meta = CONDITION_META[deletion.condition]
      return meta ? `${meta.code} · ${meta.label}` : deletion.condition
    },
    freeSinceLabel(deletion) {
      if (!deletion || deletion.days_left == null) return '—'
      const days = Math.max(0, Number(deletion.days_left))
      return `free for ${days}d`
    },
    deadlineLabel(deletion) {
      if (!deletion || !deletion.deadline) return null
      const bits = String(deletion.deadline).split('-')
      if (bits.length !== 3) return deletion.deadline
      return `${bits[2]}/${bits[1]}/${bits[0]}`
    },
    deadlineDays(deletion) {
      if (!deletion || deletion.days_to_deadline == null) return null
      return Number(deletion.days_to_deadline)
    },
    deadlineUrgency(deletion) {
      const days = this.deadlineDays(deletion)
      if (days == null) return null
      if (days <= 3) return 'red'
      if (days <= 7) return 'amber'
      return null
    },
    addedByLabel(deletion) {
      if (!deletion || !deletion.last_added_by) return null
      const added = deletion.last_added_by
      const days = added.days
      const name = added.name || 'AM'
      if (days == null) return null
      return `+${days}d added by ${name}`
    },
    onAmChange() {
      this.page = 1
      this.fullLoading = true
      this.load()
    },
    onTabChange(tab) {
      if (this.tab === tab) return
      this.tab = tab
      this.condition = null
      this.page = 1
      this.load()
    },
    onConditionChange(code) {
      if (this.condition === code) return
      this.condition = code
      this.page = 1
      this.load()
    },
    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.page = 1
        this.load()
      }, 400)
    },
    onSizeChange() {
      this.page = 1
      this.load()
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.page) return
      this.page = page
      this.load()
    },
    goToOrder(orderId) {
      if (!orderId) return
      this.$router.push(`/orders/${orderId}`)
    },
    onAddDays(teamDeletionId) {
      if (!teamDeletionId) return
      this.postponeTeamDeletionId = teamDeletionId
      this.postponeDaysInput = 7
      this.postponeError = ''
      this.postponing = false
      this.showPostponeModal = true
      this.$nextTick(() => {
        if (this.$refs.postponeInput) {
          this.$refs.postponeInput.focus()
          this.$refs.postponeInput.select()
        }
      })
    },
    closePostponeModal() {
      if (this.postponing) return
      this.showPostponeModal = false
      this.postponeTeamDeletionId = null
      this.postponeError = ''
    },
    onPostponeKeydown(event) {
      const blocked = ['-', '+', 'e', 'E', ',', '.']
      if (blocked.includes(event.key)) {
        event.preventDefault()
      }
    },
    onPostponeInput(event) {
      let cleaned = (event.target.value || '').replace(/[^0-9]/g, '')
      if (cleaned !== '' && Number(cleaned) > this.postponeMaxDays) {
        cleaned = String(this.postponeMaxDays)
      }
      if (cleaned !== event.target.value) {
        event.target.value = cleaned
      }
      this.postponeDaysInput = cleaned === '' ? '' : Number(cleaned)
      this.postponeError = ''
    },
    applyPostpone() {
      if (this.postponing || !this.isValidPostponeDays || !this.postponeTeamDeletionId) return
      this.postponing = true
      this.postponeError = ''
      teamsOverviewApi
        .postponeDeletion({
          team_ids: [this.postponeTeamDeletionId],
          days: Number(this.postponeDaysInput),
        })
        .then(() => {
          this.postponing = false
          this.showPostponeModal = false
          this.postponeTeamDeletionId = null
          this.load()
        })
        .catch((err) => {
          this.postponing = false
          this.postponeError =
            typeof err === 'string' ? err : 'Unable to postpone deletion. Please try again.'
        })
    },
  },
}
</script>

<style scoped>
.tgv-wrap {
  padding: 24px 28px 40px;
  color: #1c1c28;
}

.tgv-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}

.tgv-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9a9aab;
}

.tgv-title {
  font-size: 26px;
  font-weight: 700;
  margin: 4px 0 2px;
}

.tgv-sub {
  font-size: 13px;
  color: #8a8a9a;
  margin: 0;
}

.tgv-am-filter {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tgv-am-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #9a9aab;
  margin: 0;
}

.tgv-select-wrap {
  position: relative;
  display: inline-block;
}

.tgv-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  min-width: 180px;
  padding: 9px 34px 9px 14px;
  border: 0.5px solid #e0e0e8;
  border-radius: 10px;
  background: #fff;
  font-size: 13px;
  font-weight: 600;
  color: #1c1c28;
  cursor: pointer;
}

.tgv-select:focus {
  outline: none;
  border-color: #4b4be1;
}

.tgv-select-caret {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 10px;
  color: #9a9aab;
  pointer-events: none;
}

.tgv-cards {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin: 22px 0 8px;
}

.tgv-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 18px 18px 16px;
  border: 0.5px solid #e8e8ee;
  border-top: 3px solid #d7d7e0;
  border-radius: 12px;
  background: #fff;
}

.tgv-card--green {
  border-top-color: #0f6e56;
}

.tgv-card--amber {
  border-top-color: #c78a0a;
}

.tgv-card--red {
  border-top-color: #c62828;
}

.tgv-card-value {
  font-size: 30px;
  font-weight: 700;
  line-height: 1;
}

.tgv-card--green .tgv-card-value {
  color: #0f6e56;
}

.tgv-card--amber .tgv-card-value {
  color: #c78a0a;
}

.tgv-card--red .tgv-card-value {
  color: #c62828;
}

.tgv-card-label {
  font-size: 12.5px;
  color: #6b6b7b;
}

.tgv-tabs {
  display: flex;
  align-items: center;
  gap: 24px;
  border-bottom: 0.5px solid #e8e8ee;
  margin-top: 18px;
}

.tgv-tab {
  background: none;
  border: none;
  padding: 12px 2px;
  font-size: 13.5px;
  font-weight: 600;
  color: #6b6b7b;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.tgv-tab:hover {
  color: #1c1c28;
}

.tgv-tab--active {
  color: #d63384;
  border-bottom-color: #d63384;
}

.tgv-tab-count {
  font-size: 11px;
  font-weight: 700;
  color: #9a9aab;
}

.tgv-tab--active .tgv-tab-count {
  color: #d63384;
}

.tgv-chips {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 18px;
}

.tgv-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border: 0.5px solid #e0e0e8;
  border-radius: 999px;
  background: #fff;
  font-size: 12.5px;
  font-weight: 600;
  color: #4b4b5a;
  cursor: pointer;
}

.tgv-chip:hover {
  border-color: #c9c9d6;
}

.tgv-chip--active {
  background: #4b4be1;
  border-color: #4b4be1;
  color: #fff;
}

.tgv-chip-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.tgv-chip-count {
  font-size: 11px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.06);
}

.tgv-chip--active .tgv-chip-count {
  background: rgba(255, 255, 255, 0.25);
}

.tgv-banner {
  display: flex;
  gap: 12px;
  margin-top: 16px;
  padding: 14px 18px;
  border: 0.5px solid #d6def7;
  border-radius: 12px;
  background: #eef2ff;
}

.tgv-banner-ico {
  color: #4b4be1;
  font-size: 16px;
  margin-top: 2px;
}

.tgv-banner-text {
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: #4b4b5a;
}

.tgv-searchbar {
  margin: 20px 0 14px;
}

.tgv-search {
  position: relative;
  max-width: 320px;
}

.tgv-search-ico {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9a9aab;
  font-size: 13px;
}

.tgv-search-input {
  width: 100%;
  padding: 10px 12px 10px 34px;
  border: 0.5px solid #e0e0e8;
  border-radius: 10px;
  font-size: 13px;
  color: #1c1c28;
}

.tgv-search-input:focus {
  outline: none;
  border-color: #4b4be1;
}

.tgv-state {
  padding: 40px 0;
  text-align: center;
  color: #8a8a9a;
  font-size: 13px;
}

.tgv-state--error {
  color: #c62828;
}

.tgv-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 16px;
  flex-wrap: wrap;
}

.tgv-footer-info {
  font-size: 12.5px;
  color: #8a8a9a;
}

.tgv-footer-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.tgv-select-wrap--sm .tgv-select--sm {
  min-width: 120px;
  padding: 7px 30px 7px 12px;
  font-size: 12.5px;
}

.tgv-pager {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tgv-pager-btn {
  width: 32px;
  height: 32px;
  border: 0.5px solid #e0e0e8;
  border-radius: 8px;
  background: #fff;
  color: #4b4b5a;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
}

.tgv-pager-btn:hover:not(:disabled) {
  border-color: #4b4be1;
  color: #4b4be1;
}

.tgv-pager-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.tgv-pager-info {
  font-size: 12.5px;
  color: #6b6b7b;
  font-weight: 600;
}

.tgv-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(28, 28, 40, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.tgv-modal {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 14px;
  padding: 22px 24px 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
}

.tgv-modal-title {
  margin: 0 0 4px;
  font-size: 17px;
  font-weight: 700;
  color: #1c1c28;
}

.tgv-modal-sub {
  margin: 0 0 16px;
  font-size: 12.5px;
  color: #6b6b7b;
}

.tgv-modal-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #9a9aab;
  margin-bottom: 6px;
}

.tgv-modal-input {
  width: 100%;
  padding: 10px 12px;
  border: 0.5px solid #e0e0e8;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #1c1c28;
  background: #fff;
}

.tgv-modal-input:focus {
  outline: none;
  border-color: #4b4be1;
}

.tgv-modal-input--error,
.tgv-modal-input--error:focus {
  border-color: #c62828;
}

.tgv-modal-input:disabled {
  background: #f5f5fa;
  color: #8a8a9a;
  cursor: not-allowed;
}

.tgv-modal-input::-webkit-outer-spin-button,
.tgv-modal-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}

.tgv-modal-input[type='number'] {
  -moz-appearance: textfield;
  appearance: textfield;
}

.tgv-modal-error {
  margin: 8px 0 0;
  font-size: 12px;
  color: #c62828;
}

.tgv-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.tgv-modal-btn {
  padding: 9px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 0.5px solid transparent;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.tgv-modal-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.tgv-modal-btn--ghost {
  background: #fff;
  border-color: #e0e0e8;
  color: #4b4b5a;
}

.tgv-modal-btn--ghost:hover:not(:disabled) {
  border-color: #c9c9d6;
  color: #1c1c28;
}

.tgv-modal-btn--primary {
  background: #4b4be1;
  border-color: #4b4be1;
  color: #fff;
}

.tgv-modal-btn--primary:hover:not(:disabled) {
  background: #3b3bd0;
  border-color: #3b3bd0;
}
</style>

<style>
.tgv-days-btn {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 8px;
  background: #eef2ff;
  border: 0.5px solid #d6def7;
  color: #4b4be1;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.tgv-days-btn:hover {
  background: #4b4be1;
  color: #fff;
  border-color: #4b4be1;
}

.tgv-free {
  color: #6b6b7b;
}
</style>
